import React, {useState} from 'react';
import styled from 'styled-components';
import {CardHeading, CardSubheading} from 'app/styles/heading';
import {numberWithThousandSeparator, getAge} from 'app/helpers/helpers';
import {formatBirthDate} from 'app/helpers/dateHelpers';
import {COLORS, FONTS, Typography, Image, TextBadge} from '@fupa/fupa-uikit';
import {
  ProfileCard,
  ClickableProfilePhoto,
  ProfileStats,
  ProfileInfoColumnContainer,
  ProfileInfoColumn,
  ProfilePhotoWrapper,
  ProfileStatsData,
} from 'app/components/profile/ProfileStyles';
import {TextWithLabel, TextWithLabelJustifyContainer} from 'app/components/textWithLabel/TextWithLabel';
import {FullscreenImage} from 'app/components/backdrop/FullscreenImage';
import {useClickTracking} from 'app/hooks/useClickTracking';
import {PhotoCamera, Visibility} from '@mui/icons-material';
import DataInfoPopper from 'app/components/profile/DataInfoPopper';

const JerseyNumber = styled(TextBadge)`
  &&& {
    width: 2rem;
    height: 2rem;
    font-size: ${FONTS['18']};
  }
`;

const NickName = styled(Typography)`
  padding-top: 0.25rem;
  text-align: center;
`;

const PlayerInfo = ({player}) => {
  const [fullscreen, setFullscreen] = useState(false);
  const [detailImage, setDetailImage] = useState(null);
  const facts = {
    team: player.currentTeam
      ? {
          name:
            player?.currentTeam?.competition?.slug === 'uebergangsliga'
              ? player.currentTeam.name.middle + ' ' + player.currentTeam?.ageGroup.name
              : player.currentTeam.name.middle,
          url: `/team/${player.currentTeam.slug}`,
          image: player.currentTeam.image,
        }
      : {name: '-', url: '', image: null},
    league: player.currentTeam
      ? {
          name: player.currentTeam.competition.middleName,
          url: `/league/${player.currentTeam.competition.slug}`,
          image: player.currentTeam.competition.image,
        }
      : {name: '-', url: '', image: null},
    birthday: player.birthday ? `${formatBirthDate(player.birthday)} (${getAge(player.birthday, true)})` : '–',
    nickName:
      player.details && player.details.nickname ? (
        <NickName variant='subheading2' component='span'>
          &bdquo;{player.details.nickname}&ldquo;
        </NickName>
      ) : null,
    height: player.details && player.details.height ? player.details.height + ' cm' : '–',
    weight: player.details && player.details.weight ? player.details.weight + ' kg' : '–',
    nationalities: player?.details?.nationalities?.length
      ? player?.details?.nationalities?.map(nation => nation.icon).join(' ')
      : '–',
    position: player.position ? player.position : '–',
    strongFoot: player.details && player.details.strongFoot !== 'unknown' ? player.details.strongFoot : '–',
    jerseyNumber:
      player.playerRole && player.playerRole.seasons && player.playerRole.seasons[0].jerseyNumber ? (
        <JerseyNumber value={player.playerRole.seasons[0].jerseyNumber.toString()} position={1} />
      ) : (
        ''
      ),
  };

  const {clicks} = useClickTracking('player', player);

  const playerPhoto = player.image ? (
    <Image
      entity='player'
      circle={true}
      src={player.image}
      ratio='1:1'
      height={100}
      width={100}
      eager={true}
      fetchPriority='high'
    />
  ) : null;
  const playerPhotoSource = player.image?.source ? (
    <ProfileStatsData>
      <PhotoCamera style={{color: COLORS.darkGrey, fontSize: 16}} />
      <Typography variant='caption1'>{player.image.source}</Typography>
    </ProfileStatsData>
  ) : (
    <ProfileStatsData />
  );

  const currentTeam = (
    <TextWithLabel
      label='Aktuelles Team'
      data={facts.team.name}
      link={facts.team.url}
      img={{src: facts.team.image, type: 'emblem'}}
      decoration={facts.team.name !== '-'}
    />
  );
  const currentLeague = (
    <TextWithLabel
      alignment='right'
      noPaddingBottom={true}
      label='Aktuelle Liga'
      data={facts.league.name}
      link={facts.league.url}
      img={{src: facts.league.image, type: 'competition'}}
      decoration={facts.league.name !== '-'}
    />
  );

  const handleClose = () => {
    setFullscreen(false);
  };

  const handleOpen = detailImage => {
    setFullscreen(true);
    setDetailImage(detailImage);
  };

  return (
    <ProfileCard>
      <CardHeading>
        <CardSubheading variant='subheading2'>Spielerdaten</CardSubheading>
        <DataInfoPopper />
      </CardHeading>
      <TextWithLabelJustifyContainer>
        <ProfileInfoColumnContainer>
          {/*  1st Row - 1st Column*/}
          <ProfileInfoColumn>
            <TextWithLabel label='Position' data={facts.position} />
            {currentTeam}
          </ProfileInfoColumn>
          {/*  1st Row - 2nd Column*/}
          <ProfilePhotoWrapper>
            <div>
              <ClickableProfilePhoto onClick={() => handleOpen(player.image)}>
                {facts.jerseyNumber}
                {playerPhoto}
              </ClickableProfilePhoto>
              <FullscreenImage entity='player' imageSrc={detailImage} onClick={handleClose} open={!!fullscreen} />
            </div>
            {facts.nickName}
          </ProfilePhotoWrapper>
          {/*  1st Row - 3td Column*/}
          <ProfileInfoColumn>
            <TextWithLabel label='Starker Fuß' data={facts.strongFoot} alignment='right' />
            {currentLeague}
          </ProfileInfoColumn>
        </ProfileInfoColumnContainer>
      </TextWithLabelJustifyContainer>
      {/*  2nd Row */}
      <CardSubheading variant='subheading2'>Persönliche Daten</CardSubheading>
      <TextWithLabelJustifyContainer justify='space-between'>
        <TextWithLabel noPaddingBottom={true} label='Geburtstag' data={facts.birthday} />
        <TextWithLabel noPaddingBottom={true} label='Größe' data={facts.height} />
        <TextWithLabel noPaddingBottom={true} label='Gewicht' data={facts.weight} />
        <TextWithLabel noPaddingBottom={true} label='Nationalität' data={facts.nationalities} />
      </TextWithLabelJustifyContainer>
      {/*  3td Row */}
      <ProfileStats>
        {playerPhotoSource}
        <ProfileStatsData>
          <Visibility style={{color: COLORS.darkGrey, fontSize: 16}} />
          <Typography variant='caption1'>{numberWithThousandSeparator(clicks)}</Typography>
        </ProfileStatsData>
      </ProfileStats>
    </ProfileCard>
  );
};

export default PlayerInfo;
